import React, {
	forwardRef,
	type ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import { StatusAnnouncement } from '../status-announcement';

export const modalStatusAnnouncementDelay = 100;

/**
 * Keeps the modal's live region empty briefly after it mounts before adding
 * its status message. The region therefore exists in the modal accessibility
 * tree before assistive technology receives the update.
 */
export type StatusAnnouncementController = {
	announce: (message: ReactNode) => void;
	clear: () => void;
};

export const DelayedStatusAnnouncement: React.ForwardRefExoticComponent<
	{ children?: ReactNode } & React.RefAttributes<StatusAnnouncementController>
> = forwardRef<StatusAnnouncementController, { children?: ReactNode }>(
	function DelayedStatusAnnouncement({ children }, ref): React.JSX.Element {
		const [announcement, setAnnouncement] = useState<ReactNode>('');
		const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

		const clear = useCallback((): void => {
			if (timeoutRef.current !== null) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			setAnnouncement('');
		}, []);

		const announce = useCallback(
			(message: ReactNode): void => {
				clear();
				if (message === undefined || message === null) {
					return;
				}
				timeoutRef.current = setTimeout(() => {
					timeoutRef.current = null;
					setAnnouncement(message);
				}, modalStatusAnnouncementDelay);
			},
			[clear],
		);

		useImperativeHandle(ref, () => ({ announce, clear }), [announce, clear]);

		useEffect(() => {
			announce(children);

			return clear;
		}, [announce, clear, children]);

		return <StatusAnnouncement>{announcement}</StatusAnnouncement>;
	},
);
